import React, { useCallback } from 'react';
import {
  ListItemText,
  TableList,
  ListItem,
} from 'app/components/ChakraTableList';
import { SharedLink } from 'types/shares.types';
import { ReactComponent as CopyIcon } from 'resources/images/copy.svg';
import { ReactComponent as UnionIcon } from 'resources/images/union.svg';
import { format } from 'date-fns';
import { dateFormats } from 'app/constants';
import { Box, Icon, Link, Skeleton } from '@chakra-ui/react';
import { Link as LinkType } from 'types/api.types';
import { isLinkRevoked } from 'app/components/Shares/utils';
import { useInfiniteLoader } from 'app/utils/hooks';
import InfiniteScroll from 'react-infinite-scroll-component';
import { AppState, useAppDispatch } from 'app/store';
import { addAlertAction } from 'app/store/Alerts/slice';
import { UpdateSharedLinkEffectData } from 'app/store/Shares/effects.types';
import { GetSharedListEffectProps } from '../types';
import { ShareLinkToggle } from './ShareLinkToggle';

export interface PreviewClickProps {
  guid: string;
  shareCount: number;
  creditsShared: boolean;
  lyricsShared: boolean;
  downloadable: boolean;
}

interface ShareLinkListProps {
  sharedLinks: SharedLink[];
  revokeLink: (linkGuid: string, link: LinkType) => void;
  updateSharedLink: (
    link: LinkType,
    data: UpdateSharedLinkEffectData['data'],
    linkGuid?: string
  ) => Promise<SharedLink | void>;
  getSharedListEffect: GetSharedListEffectProps;
  getSharedLinkListNextLink: (state: AppState) => LinkType | undefined;
  loading: boolean;
  guid: string;
  onPreviewClick: (_: PreviewClickProps) => void;
}

const listId = 'share-link-list-id';

export const ShareLinkList: React.FC<ShareLinkListProps> = ({
  sharedLinks,
  updateSharedLink,
  getSharedListEffect,
  getSharedLinkListNextLink,
  revokeLink,
  loading,
  guid,
  onPreviewClick,
}) => {
  const dispatch = useAppDispatch();
  const onRowClickCallback = useCallback(
    ({ rowData }: { rowData: { link: SharedLink } }) => {
      const copyLinkToClipboard = (link: string) => {
        navigator.clipboard.writeText(link);
        dispatch(addAlertAction({ message: 'Link copied to Clipboard' }));
      };

      if (!isLinkRevoked(rowData.link)) copyLinkToClipboard(rowData.link.href);
    },
    [dispatch]
  );

  const [, infiniteScrollProps, loadingMore] = useInfiniteLoader({
    listId,
    dataLength: sharedLinks?.length,
    nextLinkSelector: getSharedLinkListNextLink,
    fetchNext: (link) =>
      getSharedListEffect({
        guid,
        next: link,
      }),
  });

  return (
    <Box
      id={listId}
      {...((sharedLinks.length || loading) && {
        width: '100%',
        height: '100%',
        overflowY: 'auto',
        px: 2,
        maxHeight: loading ? undefined : 300,
      })}
    >
      <InfiniteScroll {...infiniteScrollProps}>
        <TableList
          loadingOptions={{
            loading,
            loadingMore,
            repeat: 1,
            loader: {
              link: <Skeleton width="100%" height={6} />,
              download: <Skeleton width="50px" height={6} />,
              lyrics: <Skeleton width="50px" height={6} />,
              credits: <Skeleton width="50px" height={6} />,
            },
          }}
          columns={[
            { key: 'title', name: 'Title' },
            { key: 'download', name: 'Download', width: '18%' },
            { key: 'lyrics', name: 'Lyrics', width: '18%' },
            { key: 'credits', name: 'Credits', width: '18%' },
            { key: 'copy', name: '', width: '18%' },
            { key: 'options', name: '', contextMenu: true },
          ]}
          onRowClick={onRowClickCallback}
          rows={sharedLinks.map((link) => {
            const isRevoked = isLinkRevoked(link);
            const menuOptions =
              !isRevoked && link._links.revokeShare
                ? [
                    {
                      title: 'Revoke',
                      value: 'Revoke',
                      onClick: () => {
                        if (link._links?.revokeShare)
                          revokeLink(link.guid, link._links?.revokeShare);
                      },
                    },
                  ]
                : [];
            return {
              rowData: { link, options: menuOptions },
              disabled: isRevoked,
              clickable: !isRevoked,
              title: (
                <ListItemText
                  data-testid="share-link-title"
                  primary={
                    <Link
                      display="flex"
                      gap={1}
                      alignItems="center"
                      textDecoration="none"
                      target="_blank"
                      href={link.href}
                      title="Preview"
                      _hover={{}}
                      _focus={{}}
                      onClick={(e) => {
                        e.stopPropagation();
                        onPreviewClick({
                          guid,
                          shareCount: sharedLinks.length,
                          creditsShared: link.creditsShared,
                          lyricsShared: link.lyricsShared,
                          downloadable: link.downloadable,
                        });
                      }}
                    >
                      {link.title}
                      <Icon color="iconBase" as={UnionIcon} w={3} h={3} />
                    </Link>
                  }
                  secondary={format(
                    link.created.timestamp * 1000,
                    dateFormats.LIST_FORMAT
                  )}
                  secondaryTextProps={{
                    fontSize: 'xs',
                    lineHeight: '14px',
                    'data-testid': 'share-link-date',
                    color: 'textSubdued',
                  }}
                />
              ),
              download: (
                <ListItem w="full">
                  <ShareLinkToggle
                    propKey="download"
                    link={link}
                    isRevoked={isRevoked}
                    data={{ downloadable: !link.downloadable }}
                    updateSharedLink={updateSharedLink}
                  />
                </ListItem>
              ),
              lyrics: (
                <ShareLinkToggle
                  propKey="lyrics"
                  link={link}
                  isRevoked={isRevoked}
                  data={{ lyricsShared: !link.lyricsShared }}
                  updateSharedLink={updateSharedLink}
                />
              ),
              credits: (
                <ShareLinkToggle
                  propKey="credits"
                  link={link}
                  isRevoked={isRevoked}
                  data={{ creditsShared: !link.creditsShared }}
                  updateSharedLink={updateSharedLink}
                />
              ),
              copy: (
                <ListItem
                  data-testid="share-link-copy-icon"
                  title="Copy"
                  color="B.80"
                  gap={2}
                >
                  <Icon as={CopyIcon} color="iconBase" height={5} width={5} />
                  Copy link
                </ListItem>
              ),
            };
          })}
        />
      </InfiniteScroll>
    </Box>
  );
};
